import type {
  PaginatedResponse,
  TaskDataType,
  TaskType,
} from "types/DataTypes";

import { api } from "./api";

type TaskQueryArgs = {
  searchTerm?: string;
  file_id?: string;
  assignee_id?: string | number;
  filters?: Record<string, unknown>;
  status?: string;
};

type TaskPageParam = {
  limit: number;
  offset: number;
};

export const taskAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getTasks: build.query<PaginatedResponse<TaskDataType>, TaskQueryArgs>({
      query: ({ searchTerm, file_id, assignee_id, filters = {} }) => ({
        url: `api/v1/tasks`,
        method: "GET",
        params: Object.entries({
          ...(searchTerm && { filter: searchTerm }),
          ...(file_id && { file_id }),
          ...(assignee_id && { assignee_id }),
          ...filters,
        }).reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => acc.append(key, v));
          } else if (value !== undefined && value !== null) {
            acc.append(key, value as string);
          }
          return acc;
        }, new URLSearchParams()),
      }),
      providesTags: ["Tasks"],
    }),
    getTasksInfinite: build.infiniteQuery<
      PaginatedResponse<TaskDataType>,
      TaskQueryArgs,
      TaskPageParam
    >({
      infiniteQueryOptions: {
        initialPageParam: { limit: 25, offset: 0 },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return {
            ...lastPageParam,
            offset: lastPageParam.offset + lastPageParam.limit,
          };
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          if (firstPageParam.offset <= 0) return undefined;
          return {
            ...firstPageParam,
            offset: Math.max(0, firstPageParam.offset - firstPageParam.limit),
          };
        },
        maxPages: 10,
      },
      query: ({ queryArg, pageParam }) => {
        const {
          searchTerm,
          file_id,
          assignee_id,
          filters = {},
          status,
        } = queryArg;

        // build the query params
        const params = Object.entries({
          ...(searchTerm && { filter: searchTerm }),
          ...(file_id && { file_id }),
          ...(assignee_id && { assignee_id }),
          ...(status && { status }),
          limit: pageParam.limit.toString(),
          offset: pageParam.offset.toString(),
          ...filters,
          // reduce the params to a URLSearchParams object
        }).reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => acc.append(key, v));
          } else if (value !== undefined && value !== null) {
            acc.append(key, value as string);
          }
          return acc;
        }, new URLSearchParams());

        return {
          url: `api/v1/tasks`,
          method: "GET",
          params,
        };
      },
      providesTags: ["Tasks"],
    }),
    patchTaskById: build.mutation({
      query: ({ id, data }) => ({
        url: `api/v1/tasks/${id}/change_status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Tasks"],
    }),
    patchTaskByIdOptimistic: build.mutation({
      query: ({ id, data }) => ({
        url: `api/v1/tasks/${id}/change_status`,
        method: "PATCH",
        body: data,
      }),
      // No invalidatesTags for optimistic updates
    }),
    updateTaskById: build.mutation({
      query: ({ id, data }) => ({
        url: `api/v1/tasks/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Tasks"],
    }),
    createTask: build.mutation<TaskDataType, { data: TaskType }>({
      query: ({ data }) => ({
        url: `api/v1/tasks/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Tasks"],
    }),
    deleteTask: build.mutation({
      query: ({ id }) => ({
        url: `api/v1/tasks/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTasksQuery,
  usePatchTaskByIdMutation,
  usePatchTaskByIdOptimisticMutation,
  useCreateTaskMutation,
  useUpdateTaskByIdMutation,
  useDeleteTaskMutation,
} = taskAPI;

export const useGetTasksInfiniteQuery =
  taskAPI.endpoints.getTasksInfinite.useInfiniteQuery;
